import { z } from "zod";
import { ZONA_HORARIA } from "./dominio.js";

/**
 * Fechas "solo día" (próxima revisión, retención). Se calculan sobre el día calendario de
 * Honduras, no sobre el instante en UTC: aprobado el 26 abr a las 19:30 de Tegucigalpa ya es 27
 * en UTC, y la próxima revisión tiene que ser el 26 abr del año siguiente. El resultado es la
 * medianoche UTC de ese día, que es como Postgres guarda y devuelve una columna `date`.
 */
const partesDia = new Intl.DateTimeFormat("en-CA", {
  timeZone: ZONA_HORARIA,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

type DiaCalendario = { anio: number; mes: number; dia: number };

/** El día calendario en Honduras de un instante. */
export function diaLocal(instante: Date): DiaCalendario {
  const p = Object.fromEntries(partesDia.formatToParts(instante).map((x) => [x.type, x.value]));
  return { anio: Number(p.year), mes: Number(p.month), dia: Number(p.day) };
}

/** Hoy en Honduras, como fecha "solo día" (medianoche UTC). */
export function hoyLocal(ahora: Date = new Date()): Date {
  const { anio, mes, dia } = diaLocal(ahora);
  return new Date(Date.UTC(anio, mes - 1, dia));
}

/** Milisegundos de un día: lo que separa dos fechas "solo día" consecutivas. */
export const MS_POR_DIA = 86_400_000;

const PATRON_DIA_ISO = /^\d{4}-\d{2}-\d{2}$/;

/** "2025-08-14" es un día que existe: JavaScript acepta el 30 de febrero y lo corre a marzo. */
export function esDiaReal(dia: string): boolean {
  if (!PATRON_DIA_ISO.test(dia)) return false;
  const d = new Date(`${dia}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === dia;
}

/**
 * AAAA-MM-DD, un día que existe: una fecha tal como la dice el papel (la aprobación de un
 * formato o de una revisión importada, el primer día de una frecuencia). `mensaje` es lo que se
 * le dice a quien la deja en blanco o mal escrita.
 */
export function esquemaFechaPapel(mensaje = "Escribe la fecha de aprobación") {
  return z.string().regex(PATRON_DIA_ISO, mensaje).refine(esDiaReal, "Esa fecha no existe");
}

/** El día de una fecha de columna `date` o de `hoyLocal()`, como AAAA-MM-DD. */
export function diaIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** El día (medianoche UTC) de una fecha "AAAA-MM-DD". */
export function diaDesdeIso(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/** Suma días a una fecha "solo día" (medianoche UTC). */
export function sumarDias(dia: Date, dias: number): Date {
  return new Date(dia.getTime() + dias * MS_POR_DIA);
}

/**
 * El instante que se guarda para una fecha que dice un papel (la aprobación de una revisión
 * importada): mediodía en Honduras, para que la zona horaria no corra el día; si el papel es
 * de hoy y todavía no es mediodía, ahora. Null si el día es futuro en Honduras.
 */
export function instanteDelPapel(dia: string, ahora: Date = new Date()): Date | null {
  if (dia > diaIso(hoyLocal(ahora))) return null;
  const mediodia = new Date(`${dia}T18:00:00Z`);
  return mediodia.getTime() > ahora.getTime() ? ahora : mediodia;
}

/**
 * Suma meses a un día calendario sin desbordar: 31 ene + 1 mes = 28 (o 29) feb, no 3 mar;
 * 29 feb + 12 meses = 28 feb.
 */
function sumarMeses({ anio, mes, dia }: DiaCalendario, meses: number): Date {
  const destino = new Date(Date.UTC(anio, mes - 1 + meses, 1));
  const ultimo = new Date(
    Date.UTC(destino.getUTCFullYear(), destino.getUTCMonth() + 1, 0),
  ).getUTCDate();
  return new Date(Date.UTC(destino.getUTCFullYear(), destino.getUTCMonth(), Math.min(dia, ultimo)));
}

/** Próxima revisión anual = día de aprobación + 1 año (26 abr 2026 → 26 abr 2027). */
export function proximaRevisionAnual(aprobadaEn: Date): Date {
  return sumarMeses(diaLocal(aprobadaEn), 12);
}

const fmtFecha = new Intl.DateTimeFormat("es-HN", {
  timeZone: ZONA_HORARIA,
  day: "numeric",
  month: "short",
  year: "numeric",
});

const fmtFechaHora = new Intl.DateTimeFormat("es-HN", {
  timeZone: ZONA_HORARIA,
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** Intl abrevia septiembre como "sept."; el mockup y el resto de meses usan tres letras. */
function limpiar(texto: string): string {
  return texto.replace(".", "").replace("sept", "sep");
}

/** "26 abr 2026" */
export function formatearFecha(d: Date): string {
  return limpiar(fmtFecha.format(d));
}

/** "3 sep 2026, 09:14" */
export function formatearFechaHora(d: Date): string {
  return limpiar(fmtFechaHora.format(d));
}

const fmtHora = new Intl.DateTimeFormat("es-HN", {
  timeZone: ZONA_HORARIA,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "07:02", en la hora de Honduras. */
export function formatearHora(d: Date): string {
  return fmtHora.format(d);
}

/**
 * Fecha "solo día" (columnas `date`: próxima revisión, retención). Se formatea en UTC porque
 * Postgres la entrega como medianoche UTC; pasarla por la zona de Honduras la corría un día.
 */
const fmtDia = new Intl.DateTimeFormat("es-HN", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** "26 abr 2027" para una fecha sin hora. */
export function formatearDia(d: Date): string {
  return limpiar(fmtDia.format(d));
}

/** Día en que un registro cumple su retención: día de aprobación + meses de retención. */
export function fechaDeRetencion(aprobadaEn: Date, retencionMeses: number): Date {
  return sumarMeses(diaLocal(aprobadaEn), retencionMeses);
}

/**
 * Hasta cuándo se conserva una hoja llena (fase 3b): el día de la hoja + la retención del
 * formato. La fecha de la hoja es una columna `date` (medianoche UTC): se toma tal cual, sin
 * pasarla por la zona de Honduras, que la correría un día.
 */
export function retencionDeDia(dia: Date, retencionMeses: number): Date {
  return sumarMeses(
    { anio: dia.getUTCFullYear(), mes: dia.getUTCMonth() + 1, dia: dia.getUTCDate() },
    retencionMeses,
  );
}

/** Días enteros entre `desde` y `hasta` (negativo si `hasta` ya pasó). */
export function diasHasta(hasta: Date, desde: Date = new Date()): number {
  return Math.floor((hasta.getTime() - desde.getTime()) / MS_POR_DIA);
}

/** Medianoche UTC exacta: así llegan las columnas `date` y lo que devuelve `hoyLocal()`. */
function esSoloDia(d: Date): boolean {
  return d.getTime() % MS_POR_DIA === 0;
}

/** "hace 2 días", "hoy", "en 3 días": la segunda línea de la lista maestra. */
export function relativo(d: Date, ahora: Date = new Date()): string {
  // Por día de calendario, no por instantes: algo de hace cinco minutos es "hoy", no "ayer"
  // (con instantes, floor(-5 min / día) da -1). Una fecha "solo día" es su propio día.
  const dia = (x: Date) => (esSoloDia(x) ? x : hoyLocal(x));
  const dias = diasHasta(dia(d), dia(ahora));
  if (dias === 0) return "hoy";
  if (dias === -1) return "ayer";
  if (dias === 1) return "mañana";
  return dias < 0 ? `hace ${-dias} días` : `en ${dias} días`;
}
